import type { CsvAnalyzeResponse, CsvImportResponse, CsvParseResponse } from '@shared/schemas';

import { type DataSourceDraft, EMPTY_DATA_SOURCE } from '../domain/data-source';
import type { FileImportError } from '../domain/file-import-error';

/**
 * Redux state for the standalone file-import flow (Upload → Map Columns →
 * Analyze → Results). One slice serves both routes: admin (`listId` set,
 * imports into an existing list) and client (`listId` null, the import creates
 * a new list named `importName`). Every field is serialisable; the uploaded
 * `File` never enters the store, only its name does (the saga holds the File
 * for the duration of the parse call).
 */
export interface FileImportState {
  /** Admin mode: the target list. Client mode: null. */
  listId: string | null;
  /** 0 = Upload, 1 = Map Columns, 2 = Analyze, 3 = Results. */
  step: number;

  // ── Upload ──
  /** Client mode only: name of the list the import creates. */
  importName: string;
  /**
   * Client mode only (#749): where the file's data came from. Pre-filled from
   * the licence's last import once the form is reset.
   */
  dataSource: DataSourceDraft;
  fileName: string | null;
  parsing: boolean;
  parseResult: CsvParseResponse | null;

  // ── Map columns ──
  columnMapping: Record<string, string>;

  // ── Analyze ──
  analyzing: boolean;
  analyzeResult: CsvAnalyzeResponse | null;
  /** "Data is pre-enriched" switch on the analyze step. */
  isEnriched: boolean;
  /**
   * Admin mode (#709): the rows whose exit conflict the administrator chose to
   * overwrite, row by row. Every other conflicting row is skipped.
   */
  overwriteRows: number[];

  // ── Results ──
  importing: boolean;
  importResult: CsvImportResponse | null;

  error: FileImportError | null;
}

export const initialState: FileImportState = {
  listId: null,
  step: 0,
  importName: '',
  dataSource: EMPTY_DATA_SOURCE,
  fileName: null,
  parsing: false,
  parseResult: null,
  columnMapping: {},
  analyzing: false,
  analyzeResult: null,
  isEnriched: false,
  overwriteRows: [],
  importing: false,
  importResult: null,
  error: null,
};
